/**
 * Provider Number Validation Utilities
 *
 * This module provides country-specific provider number validation
 * for medical professionals in the BiteWise provider registration system.
 * Supports various international medical provider identification formats.
 *
 * @module utils/validation/providerNumberValidation
 */

import type { ValidationResult } from '@/types/registration';

/**
 * Provider number validation configuration
 */
const PROVIDER_CONFIG = {
  /** Default country for provider validation */
  DEFAULT_COUNTRY: 'AU'
} as const;

/**
 * Provider number validation error messages
 */
const PROVIDER_ERRORS = {
  REQUIRED: 'Provider number is required',
  INVALID_FORMAT: 'Invalid provider number format',
  INVALID_COUNTRY: 'Unsupported country for provider validation',
  TOO_SHORT: 'Provider number is too short',
  TOO_LONG: 'Provider number is too long',
  INVALID_CHARACTERS: 'Provider number contains invalid characters',
  INVALID_CHECKSUM: 'Provider number checksum is invalid'
} as const;

/**
 * Country-specific provider number validation rules
 */
const PROVIDER_VALIDATION_RULES: Record<string, {
  pattern: RegExp;
  length: number | { min: number; max: number };
  format: string;
  example: string;
  description: string;
  validate?: (providerNumber: string) => ValidationResult;
}> = {
  AU: {
    pattern: /^\d{10}$/,
    length: 10,
    format: '10 digits',
    example: '1234567890',
    description: 'Australian Medicare Provider Number',
    validate: validateAustralianProviderNumber
  },
  US: {
    pattern: /^\d{10}$/,
    length: 10,
    format: '10 digits',
    example: '1234567890',
    description: 'US Provider Number (10 digits only)'
  },
  GB: {
    pattern: /^[A-Z]\d{6}$/,
    length: 7,
    format: '1 letter + 6 digits',
    example: 'G1234567',
    description: 'UK General Medical Council (GMC) Number',
    validate: validateUKGMC
  },
  CA: {
    pattern: /^\d{5}$/,
    length: 5,
    format: '5 digits',
    example: '12345',
    description: 'Canadian Medical License Number',
    validate: validateCanadianMedicalLicense
  },
  NZ: {
    pattern: /^\d{6}$/,
    length: 6,
    format: '6 digits',
    example: '123456',
    description: 'New Zealand Medical Council Number'
  },
  DE: {
    pattern: /^\d{9}$/,
    length: 9,
    format: '9 digits',
    example: '123456789',
    description: 'German Medical License Number (Arztnummer)'
  }
};

/**
 * Validates provider number according to country-specific rules
 *
 * @param providerNumber - The provider number to validate
 * @param countryCode - ISO country code (defaults to AU)
 * @returns ValidationResult with isValid flag and error message if invalid
 *
 * @example
 * ```typescript
 * const result = validateProviderNumber('1234567890', 'AU');
 * if (result.isValid) {
 *   logger.log('Provider number is valid');
 * } else {
 *   logger.log('Error:', result.error);
 * }
 * ```
 */
export const validateProviderNumber = (
  providerNumber: string,
  countryCode: string = PROVIDER_CONFIG.DEFAULT_COUNTRY
): ValidationResult => {
  // Check if provider number is provided
  if (!providerNumber || providerNumber.trim().length === 0) {
    return {
      isValid: false,
      error: PROVIDER_ERRORS.REQUIRED
    };
  }

  const trimmedNumber = providerNumber.trim().toUpperCase();

  // Check if country is supported
  const rule = PROVIDER_VALIDATION_RULES[countryCode];
  if (!rule) {
    return {
      isValid: false,
      error: `${PROVIDER_ERRORS.INVALID_COUNTRY}. Supported countries: ${Object.keys(PROVIDER_VALIDATION_RULES).join(', ')}`
    };
  }

  // Check length
  if (typeof rule.length === 'number') {
    if (trimmedNumber.length !== rule.length) {
      return {
        isValid: false,
        error: `${PROVIDER_ERRORS.INVALID_FORMAT}. Expected format: ${rule.format} (Example: ${rule.example})`
      };
    }
  } else {
    if (trimmedNumber.length < rule.length.min || trimmedNumber.length > rule.length.max) {
      return {
        isValid: false,
        error: `${PROVIDER_ERRORS.INVALID_FORMAT}. Expected length: ${rule.length.min}-${rule.length.max} characters`
      };
    }
  }

  // Check pattern
  if (!rule.pattern.test(trimmedNumber)) {
    return {
      isValid: false,
      error: `${PROVIDER_ERRORS.INVALID_FORMAT}. Expected format: ${rule.format} (Example: ${rule.example})`
    };
  }

  // Country-specific validation
  if (rule.validate) {
    return rule.validate(trimmedNumber);
  }

  return { isValid: true };
};

/**
 * Validates Australian Medicare Provider Number
 * Format: 10 digits (e.g., 1234567890)
 *
 * @param providerNumber - Australian provider number
 * @returns ValidationResult
 */
function validateAustralianProviderNumber(providerNumber: string): ValidationResult {
  // Basic format already validated by pattern
  // Additional validation could include:
  // - Check against known invalid ranges
  // - Check for sequential numbers (which might be test numbers)

  // Check for obviously invalid patterns
  if (providerNumber === '0000000000' || providerNumber === '9999999999') {
    return {
      isValid: false,
      error: 'Invalid provider number range'
    };
  }

  // Check for sequential numbers (likely test numbers)
  if (providerNumber === '1234567890' || providerNumber === '0123456789') {
    return {
      isValid: false,
      error: 'Invalid provider number - appears to be a test number'
    };
  }

  return { isValid: true };
}


/**
 * Validates UK General Medical Council (GMC) Number
 * Format: 1 letter + 6 digits
 *
 * @param providerNumber - UK GMC number
 * @returns ValidationResult
 */
function validateUKGMC(providerNumber: string): ValidationResult {
  // GMC numbers typically start with specific letters
  const validStartLetters = ['G', 'C', 'D', 'F', 'M', 'P', 'S', 'T'];
  const firstLetter = providerNumber.charAt(0);

  if (!validStartLetters.includes(firstLetter)) {
    return {
      isValid: false,
      error: `UK GMC number must start with one of: ${validStartLetters.join(', ')}`
    };
  }

  return { isValid: true };
}

/**
 * Validates Canadian Medical License Number
 * Format: 5 digits (varies by province)
 *
 * @param providerNumber - Canadian medical license number
 * @returns ValidationResult
 */
function validateCanadianMedicalLicense(providerNumber: string): ValidationResult {
  // Basic validation - could be enhanced with province-specific rules
  const number = parseInt(providerNumber, 10);

  if (number < 10000 || number > 99999) {
    return {
      isValid: false,
      error: 'Canadian medical license must be between 10000 and 99999'
    };
  }

  return { isValid: true };
}


/**
 * Formats provider number according to country-specific display rules
 *
 * @param providerNumber - Raw provider number
 * @param countryCode - ISO country code
 * @returns Formatted provider number
 *
 * @example
 * ```typescript
 * const formatted = formatProviderNumber('1234567890', 'AU');
 * logger.log(formatted); // "1234567890"
 * ```
 */
export const formatProviderNumber = (
  providerNumber: string,
  countryCode: string = PROVIDER_CONFIG.DEFAULT_COUNTRY
): string => {
  const trimmed = providerNumber.trim().toUpperCase();

  switch (countryCode) {
    case 'AU':
      // Format: 123456 AB
      if (trimmed.length === 8) {
        return `${trimmed.substring(0, 6)} ${trimmed.substring(6)}`;
      }
      break;
    case 'US':
      // Format: 1234567890 (no special formatting for NPI)
      return trimmed;
    case 'GB':
      // Format: G 123456
      if (trimmed.length === 7) {
        return `${trimmed.substring(0, 1)} ${trimmed.substring(1)}`;
      }
      break;
    case 'CA':
      // Format: 12345 (no special formatting)
      return trimmed;
    default:
      return trimmed;
  }

  return trimmed;
};

/**
 * Gets provider number format information for a country
 *
 * @param countryCode - ISO country code
 * @returns Provider number format info or undefined
 */
export const getProviderNumberInfo = (countryCode: string) => {
  return PROVIDER_VALIDATION_RULES[countryCode];
};

/**
 * Gets example provider number for a country
 *
 * @param countryCode - ISO country code
 * @returns Example provider number
 */
export const getProviderNumberExample = (countryCode: string): string => {
  const rule = PROVIDER_VALIDATION_RULES[countryCode];
  return rule?.example || '123456';
};

/**
 * Gets supported countries for provider number validation
 *
 * @returns Array of supported country codes
 */
export const getSupportedCountries = (): string[] => {
  return Object.keys(PROVIDER_VALIDATION_RULES);
};

/**
 * Checks if a country supports provider number validation
 *
 * @param countryCode - ISO country code
 * @returns Whether the country is supported
 */
export const isProviderValidationSupported = (countryCode: string): boolean => {
  return countryCode in PROVIDER_VALIDATION_RULES;
};

/**
 * Normalizes provider number for storage
 * Removes spaces and converts to uppercase
 *
 * @param providerNumber - Provider number to normalize
 * @returns Normalized provider number
 */
export const normalizeProviderNumber = (providerNumber: string): string => {
  return providerNumber.trim().toUpperCase().replace(/\s/g, '');
};